import AgoraRTC from 'agora-rtc-sdk-ng';

import AgoraAdapterBase from './AgoraAdapterBase';

/**
 * AgoraViewerAdapter provides a wrapper around the Agora SDK for viewing live
 * streams and coordinating messaging.
 */
export default class AgoraViewerAdapter extends AgoraAdapterBase {
  constructor() {
    super();
    this.remoteVideoTrack = null;
    this.remoteAudioTrack = null;
    this.remoteUserId = null;
    this.streamActive = false;
  }

  /**
   * Initialize the adapter with configuration
   * @param {Object} options - Configuration options
   */
  async init(options) {
    // Initialize common components from base class
    await this.initCommon(options);
    this.log('Initializing AgoraViewerAdapter');

    // Set audience-specific callback handlers
    this.onVideoPlaying = options.onVideoPlaying || (() => {});
    this.onTcpAudioContextStateChange = (
      options.onTcpAudioContextStateChange || (() => { })
    );

    // Create and initialize the AgoraRTC client as audience
    this.client = AgoraRTC.createClient({
      mode: 'live',
      codec: 'vp8',
      role: 'audience',
    });

    if (!this.videoElId) {
      this.error('No video element ID provided');
      return;
    }

    // Set up event listeners for the client
    this.setupViewerClientListeners();
  }

  /**
   * Set up event listeners specific to the viewer client
   */
  setupViewerClientListeners() {
    // Call base setup first
    super.setupClientListeners();

    this.client.on('user-published', async (user, mediaType) => {
      this.remoteUserId = user.uid;
      this.log(
        `User ${user.uid} published ${mediaType} stream`,
      );

      // Subscribe to the remote user
      await this.client.subscribe(user, mediaType);

      if (mediaType === 'video') {
        this.remoteVideoTrack = user.videoTrack;

        // Play the remote video
        const videoElement = document.getElementById(this.videoElId);
        if (videoElement) {
          this.remoteVideoTrack.play(this.videoElId);
          this.streamActive = true;
          this.onVideoPlaying();
        } else {
          this.error('Video element not found');
        }
      }

      if (mediaType === 'audio') {
        this.remoteAudioTrack = user.audioTrack;
        if (!this.muted) {
          this.remoteAudioTrack.play();
        }

        // Notify audio context state
        this.onTcpAudioContextStateChange('running');
      }
    });

    this.client.on('user-unpublished', (user, mediaType) => {
      this.log(
        `User ${user.uid} unpublished ${mediaType} stream`,
      );

      if (mediaType === 'video') {
        this.remoteVideoTrack = null;
      }

      if (mediaType === 'audio') {
        this.remoteAudioTrack = null;
      }
    });

    this.client.on('user-left', (user) => {
      // Stop the stream if the remote publisher leaves
      if (user.uid !== this.remoteUserId) {
        return;
      }
      this.log(
        `User ${user.uid} left the channel, stream ended`,
      );
      this.stopStream(false, false);
    });

    this.client.on('connection-state-change', (curState, prevState) => {
      this.log(
        `Connection state changed from ${prevState} to ${curState}`,
      );

      if (curState === 'DISCONNECTED') {
        this.stopStream(false, false);
      }
    });
  }

  /**
   * View a stream
   */
  async viewStream() {
    if (!this.serverData) {
      this.onStreamError("Can't get streaming server from API...");
      return;
    }

    try {
      this.log('Joining room:', this.roomName);

      // Join the room as audience
      await this.client.join(
        this.appId,
        this.roomName,
        this.token,
        this.uid || null,
      );

      this.log('Successfully joined room');

      await this.connectChat();

      // If no stream received within 10 seconds, show error
      setTimeout(() => {
        if (!this.streamActive) {
          this.log('No stream received within timeout');
        }
      }, 10000);
    } catch (error) {
      this.error('Error joining room:', error);
      this.onStreamError(`Failed to connect to stream: ${error.message}`);
    }
  }

  /**
   * Stop the stream
   * @param {boolean} isError - Whether stopping due to error
   * @param {boolean} notifyServer - Whether to notify server
   */
  async stopStream(isError = false, isClientTriggered = true) {
    this.log(
      'Stopping stream, isError:',
      isError,
      'client triggered:',
      isClientTriggered,
    );

    this.streamActive = false;

    // Use base class to clean up chat
    await this.cleanupCommChannel();

    // Clean up remote tracks (no need to call close() on them)
    this.remoteVideoTrack = null;
    this.remoteAudioTrack = null;

    // Leave the room
    if (this.client) {
      try {
        await this.client.leave();
      } catch (error) {
        this.log('Error leaving room:', error);
      }
    }

    this.onStreamEnd(isError, isClientTriggered);
  }

  /**
   * Mute audio
   */
  muteAudio() {
    if (this.remoteAudioTrack) {
      this.remoteAudioTrack.stop();
    }
    this.muted = true;
    this.onTcpAudioContextStateChange('suspended');
  }

  /**
   * Unmute audio
   */
  unmuteAudio() {
    if (this.remoteAudioTrack) {
      this.remoteAudioTrack.play();
    }
    this.muted = false;
    this.onTcpAudioContextStateChange('running');
  }
}
